import { useQueries } from "@tanstack/react-query";
import { isAxiosError } from "axios";

import { flowDbToFlowT } from "src/api";
import { flowsApi } from "src/api/endpoints";
import { Flow } from "src/api/flowTypes";
import { flowKeys } from "src/api/queries";
import { JsonApiResourceObjectCaseTemplateVersionAttributesBaseModel as CaseTemplateVersion } from "src/clients/case-management";
import { FlowVersionStatus } from "src/clients/flow-api";
import { DashboardPageParams } from "src/router/urls";
import { useParamsDecode } from "src/utils/useParamsDecode";

type ReferencedFlowVersion = {
  flowSlug: string;
  versionName: string;
  flowId?: string;
  versionId?: string;
};

export type UnpublishedFlowVersion = ReferencedFlowVersion & {
  flowName?: string;
  status: "draft" | "archived" | "not-found";
};

export const collectReferencedFlowVersions = (
  version: CaseTemplateVersion | null,
): ReferencedFlowVersion[] => {
  if (!version) return [];

  const prepFlow = version.attributes.case_creation_flow_details;
  const prepFlowRefs: ReferencedFlowVersion[] = prepFlow
    ? [{ flowSlug: prepFlow.flow_slug, versionName: prepFlow.version_name }]
    : [];

  const actionFlowRefs: ReferencedFlowVersion[] = Object.values(
    version.attributes.case_actions ?? {},
  ).flatMap((action) =>
    action.flow
      ? [
          {
            flowSlug: action.flow.flow_slug,
            versionName: action.flow.flow_version_name,
            flowId: action.flow.flow_id,
            versionId: action.flow.flow_version_id,
          },
        ]
      : [],
  );

  // Dedupe on slug+version; a ref carrying ids wins so the deep link survives
  return Object.values(
    [...prepFlowRefs, ...actionFlowRefs].reduce<
      Record<string, ReferencedFlowVersion>
    >((refs, ref) => {
      const key = JSON.stringify([ref.flowSlug, ref.versionName]);
      return { ...refs, [key]: refs[key]?.flowId ? refs[key] : ref };
    }, {}),
  );
};

const loadReferencedFlow = async (
  workspaceId: string,
  ref: ReferencedFlowVersion,
): Promise<Flow | null> => {
  try {
    const response =
      await flowsApi.getWorkspaceFlowBySlugApiV1FlowsWorkspaceIdWorkspaceIdSlugSlugGet(
        workspaceId,
        ref.flowSlug,
        ref.versionName,
        undefined,
        undefined,
        undefined,
        undefined,
        true,
      );
    return flowDbToFlowT(response.data);
  } catch (error) {
    // Flow or matching version gone — an unpublished dependency, not a failure
    if (isAxiosError(error) && error.response?.status === 404) return null;
    throw error;
  }
};

const toUnpublishedFlowVersion = (
  ref: ReferencedFlowVersion,
  flow: Flow | null,
): UnpublishedFlowVersion[] => {
  if (!flow) {
    // 404: keep no ids, so the UI doesn't link to a missing page
    return [
      { ...ref, flowId: undefined, versionId: undefined, status: "not-found" },
    ];
  }

  const flowVersion = flow.versions?.find((v) => v.name === ref.versionName);

  if (flowVersion?.status === FlowVersionStatus.PUBLISHED) return [];

  return [
    {
      ...ref,
      flowName: flow.name,
      flowId: flow.id,
      versionId: flowVersion?.id ?? ref.versionId,
      // A version missing from the flow means it was archived
      status:
        flowVersion?.status === FlowVersionStatus.DRAFT ? "draft" : "archived",
    },
  ];
};

export const useUnpublishedFlowVersions = (
  version: CaseTemplateVersion | null,
): {
  unpublishedFlowVersions: UnpublishedFlowVersion[];
  isChecking: boolean;
  hasFailedChecks: boolean;
} => {
  const { wsId } = useParamsDecode<DashboardPageParams>();
  const referencedFlowVersions = collectReferencedFlowVersions(version);

  return useQueries({
    queries: referencedFlowVersions.map((ref) => ({
      queryKey: flowKeys.referencedVersionCheck(
        wsId,
        ref.flowSlug,
        ref.versionName,
      ),
      queryFn: () => loadReferencedFlow(wsId, ref),
      enabled: Boolean(wsId),
      // An advisory check must not escalate a 500 to the error boundary
      throwOnError: false,
    })),
    combine: (results) => ({
      isChecking: results.some((result) => result.isLoading),
      hasFailedChecks: results.some((result) => result.isError),
      // Each dependency shows up as soon as its own request settles
      unpublishedFlowVersions: results.flatMap((result, index) =>
        result.isSuccess
          ? toUnpublishedFlowVersion(referencedFlowVersions[index], result.data)
          : [],
      ),
    }),
  });
};
